<template>
  <Teleport to="#app">
    <Transition name="sync-status__transition" appear>
      <div v-if="visible && progress != 100 && total > 0" class="sync-status">
        <div class="sync-status__message">{{ $t("status.synching") }}...</div>
        <div class="sync-status__body">
          <div class="sync-status__progress">
            <div class="sync-status__info">
              <span>{{ $t("status.remaining", { remaining }) }}</span>
            </div>
            <div class="sync-status__progress-bar" :style="{ width: `${progress}%` }">
              <div class="sync-status__info">
                <span>{{ $t("status.remaining", { remaining }) }}</span>
              </div>
            </div>
          </div>
          <button type="button" class="sync-status__abort" @click="abortSynch()" :title="$t('labels.abort')">
            <OctagonXIcon :size="18" />
          </button>
        </div>
        <div class="sync-status__current-model" v-show="currentModelName" :title="currentModelName">
          <span>
            <span class="sync-status__current-model-icon"><DownloadIcon :size="16" /></span> {{ currentModelName }}
          </span>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { modelLocalAPI } from "@/api/modelAPI";
import { DownloadIcon, OctagonXIcon } from "@lucide/vue";

const synching = computed(() => modelLocalAPI.syncStatus.synching);
const remaining = computed(() => modelLocalAPI.syncStatus.remainingModels);
const total = computed(() => modelLocalAPI.syncStatus.modelsToProcess);
const processed = computed(() => total.value - remaining.value);
const currentModelName = computed(() => modelLocalAPI.syncStatus.current);

const progress = computed(() => {
  if (total.value == 0) return 0;
  return (processed.value / total.value) * 100;
});

const visible = ref(false);
const delayTimer = ref<NodeJS.Timeout | null>(null);
watch(synching, (synching) => {
  if (synching) {
    delayTimer.value = setTimeout(() => {
      if (progress.value < 50) visible.value = true;
    }, 1500);
  } else {
    visible.value = false;
    if (delayTimer.value) {
      clearTimeout(delayTimer.value);
      delayTimer.value = null;
    }
  }
});

function abortSynch() {
  modelLocalAPI.syncStatus.aborting = true;
}
</script>

<style lang="scss">

.sync-status {
  position: fixed;
  bottom: 1rem;
  right: 50%;
  transform: translateX(50%);
  min-width: 16rem;
  background: var(--color-background-deep);
  padding: 0.8rem 1rem;
  border-radius: 1rem;
  box-shadow: 0 0.25rem 1rem var(--color-shadow-3);
  z-index: 1000;
  user-select: none;
  
  &__body {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
  }

  &__message {
    margin-bottom: 0.3rem;
    color: var(--color-text-medium);
  }

  &__progress,
  &__progress-bar {
    display: flex;
    align-items: center;
    padding-left: 0.5rem;
    overflow: hidden;
  }

  &__progress {
    width: 100%;
    position: relative;
    height: 1.25rem;
    border-radius: 0.75rem;
    background-color: var(--color-background-deep);
    box-shadow: inset 0 0 5px var(--color-shadow-1);
    z-index: 0;
    font-size: 0.9em;

    &-bar {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      color: #fff;
      background-color: var(--color-primary);
      transition: width 0.2s ease;
    }
  }

  &__info {
    white-space: nowrap;
  }

  &__transition {
    &-enter-active, &-leave-active {
      transition: opacity 0.2s ease, transform 0.2s ease;
    }
    &-enter-from, &-leave-to {
      opacity: 0;
      transform: translateY(10px) translateX(50%);
    }
  }

  &__abort {
    appearance: none;
    border: none;
    background: none;
    transition: color 0.2s ease;
    border-radius: 50%;

    &:hover {
      color: var(--color-primary);
    }
  }

  &__current-model {
    position: relative;
    margin-top: 0.25rem;
    height: 1.25rem;
    width: 100%;
    color: var(--color-text-moderate);
    overflow: hidden;
    font-size: 0.9em;
    & > span {
      position: absolute;
      white-space: nowrap;
      left: 0;
      top: 0;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  &__current-model-icon {
    color: var(--color-text-medium);
    bottom: -0.25em;
    position: relative;
  }
}
</style>